import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import SettingsToggle from '../../controls/settings-toggle';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getIsSettingsUpdating,
  getWorkingProjectUuid,
  selectIsSavingWorkingProject,
  selectProjectSettings,
  selectWorkingProjectRegistrationSettings,
} from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields, updateProjectSettings, updatePublishSettings } from '@/store/slices/project';
import {
  selectCookieBanner,
  selectIsPublishLocked,
  selectMarketingEmailOptions,
} from '@/store/slices/settings/selectors';
import { EventSettings } from '@/types/projects.types';
import { MarketingOptInTypes, Registration } from '@/types/registration.types';
import ProductIdWidget from './product-id-widget';
import SessionMetadataWidget from './session-metadata-widget';
import { ChannelFeaturesEnum } from '@/types/feature-flags.types';
import useCanUseChannelFeature from '@/utils/use-can-use-channel-features';

const AdvancedSettings = () => {
  const cookieBannerEnabled = useAppSelector(selectCookieBanner);
  const marketingEmailOptions = useAppSelector(selectMarketingEmailOptions);
  const isPublishLocked = useAppSelector(selectIsPublishLocked);
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);
  const isSettingsUpdating = useAppSelector(getIsSettingsUpdating);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const projectSettings = useAppSelector(selectProjectSettings);

  const publishLockEnabled = useCanUseChannelFeature(ChannelFeaturesEnum.event_publish_locking);

  const dispatch = useAppDispatch();

  const handleCookieBannerToggle = (val: string, on: boolean) => {
    if (projectUuid && projectSettings) {
      const settings: EventSettings = {
        ...projectSettings,
        gdpr: {
          ...projectSettings.gdpr,
          enable_cookie_banner: on,
        },
      };
      dispatch(updateProjectSettings({ event_uuid: projectUuid, settings }));
    }
  };

  const handleMarketingEmailToggle = (val: string, on: boolean) => {
    if (registrationSettings) {
      const optIn = on ? MarketingOptInTypes.singleOptIn : MarketingOptInTypes.noOptIn;
      const regSettings: Registration = {
        ...registrationSettings,
        marketing_email_options: optIn,
      };
      dispatch(
        saveWorkingProjectByFields({
          registration_settings: regSettings,
          saveId: 'settings-marketing-opt-in',
        })
      );
    }
  };

  const handlePublishLockToggle = (val: string, on: boolean) => {
    if (projectUuid) {
      dispatch(
        updatePublishSettings({
          project_uuid: projectUuid,
          isLocked: on,
        })
      );
    }
  };

  return (
    <div className="flex-column gap-16">
      <div className="flex gap-4">
        <Text className="H5" color="var(--gray-100)">
          Advanced
        </Text>
        <Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--gray-500)" />
      </div>
      <SettingsToggle
        label="Cookie banner"
        value={cookieBannerEnabled}
        sKey="cookie"
        disabled={savingWorkingProject || isSettingsUpdating}
        onToggle={handleCookieBannerToggle}
      />
      <SettingsToggle
        label="Marketing email opt-in"
        value={marketingEmailOptions === MarketingOptInTypes.singleOptIn}
        sKey="email"
        disabled={savingWorkingProject || isSettingsUpdating}
        onToggle={handleMarketingEmailToggle}
      />
      <SessionMetadataWidget />
      <SettingsToggle
        label="Publish locking"
        value={isPublishLocked}
        sKey="publish-lock"
        disabled={!publishLockEnabled || savingWorkingProject || isSettingsUpdating}
        onToggle={handlePublishLockToggle}
      />
      <ProductIdWidget />
    </div>
  );
};

export default AdvancedSettings;
